import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { attendanceService } from '../services/attendanceService';
import { userService } from '../services/userService';
import EmploymentFilter, { type Employment } from '../components/EmploymentFilter';
import { attendanceCorrectionService } from '../services/attendanceCorrectionService';
import AttendanceCorrectionModal from '../components/AttendanceCorrectionModal';
import api from '../services/api';
import type { AttendanceRecord } from '../types/attendance';
import type { User } from '../types/user';
import type { AttendanceCorrectionRequest } from '../types/attendanceCorrection';

interface UserPermissions {
  canApproveCorrections: boolean;
  canApproveOwnRequests: boolean;
  canSubmitForOthers: boolean;
  attendanceViewScope: 'self' | 'department' | 'company' | 'custom';
  viewableUserIds?: number[];
}

/**
 * 打刻元のバッジ。共用端末から来た打刻にだけ付ける。
 *
 * 1行に出勤と退勤の2打刻が入るので、行単位で「共用端末の記録」とは言えない
 * （出勤は共用端末・退勤はスマホ、が普通に起きる）。どの打刻が共用端末から
 * 来たかを個別に示す。
 */
const KioskBadge: React.FC<{ deviceName?: string | null }> = ({ deviceName }) => (
  <span
    title={deviceName ? `共用端末: ${deviceName}` : '共用端末からの打刻'}
    style={{
      display: 'inline-block',
      marginLeft: 4,
      padding: '1px 5px',
      fontSize: 10,
      lineHeight: 1.6,
      color: '#0969da',
      backgroundColor: '#ddf4ff',
      border: '1px solid #b6e3ff',
      borderRadius: 4,
      whiteSpace: 'nowrap',
      verticalAlign: 'middle',
    }}
  >
    端末
  </span>
);

const AttendanceHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [employment, setEmployment] = useState<Employment>('active');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [tempSelectedUserId, setTempSelectedUserId] = useState<number | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [, setFilterDateFrom] = useState('');
  const [, setFilterDateTo] = useState('');
  const [tempFilterDateFrom, setTempFilterDateFrom] = useState('');
  const [tempFilterDateTo, setTempFilterDateTo] = useState('');
  const [, setFilterMonth] = useState(() => {
    // デフォルトで当月を設定
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [tempFilterMonth, setTempFilterMonth] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<AttendanceRecord | null>(null);
  const [pendingRequests, setPendingRequests] = useState<Map<number, AttendanceCorrectionRequest>>(new Map());
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [standardWorkHours, setStandardWorkHours] = useState<number>(8);

  useEffect(() => {
    if (user) {
      // デフォルトで本人を選択
      setSelectedUserId(user.id);
      setTempSelectedUserId(user.id);
      loadPermissions();
      loadPendingRequests();
      loadCompanySettings();
    }
  }, [user]);

  // permissions が取得された後にユーザー一覧を取得。
  // 在職状態の絞り込みを変えたときも読み直す。
  useEffect(() => {
    if (permissions && permissions.attendanceViewScope !== 'self') {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, employment]);

  const loadPermissions = async () => {
    try {
      const response = await api.get<UserPermissions>('/auth/permissions');
      setPermissions(response.data);
    } catch (err: any) {
      console.error('権限情報取得エラー:', err);
    }
  };

  const loadCompanySettings = async () => {
    try {
      const response = await api.get<{ standard_work_hours: number }>('/companies/settings');
      setStandardWorkHours(parseFloat(String(response.data.standard_work_hours)) || 8);
    } catch (err: any) {
      console.error('企業設定取得エラー:', err);
    }
  };

  useEffect(() => {
    if (user && selectedUserId) {
      // 初回ロード時のみ実行
      loadAttendanceRecords();
    }
  }, [user, selectedUserId]);

  // ページがフォーカスされた時に勤怠記録を再読み込み（承認後の変更を反映）
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user && selectedUserId) {
        loadAttendanceRecords();
        loadPendingRequests();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, selectedUserId]);

  const loadUsers = async () => {
    // 権限が自分のみ閲覧の場合は他のユーザーを取得しない
    if (!user || !permissions || permissions.attendanceViewScope === 'self') return;

    try {
      // **過去の勤怠を見るための画面**なので、退職者も選べる必要がある。
      // どちらを見たいかは目的で変わるので、既定は在職者にして切り替えられる形にする。
      const data = await userService.getUsers(employment);
      setUsers(data);
    } catch (err: any) {
      console.error('ユーザー一覧取得エラー:', err);
    }
  };

  // 部署でフィルタリング＆検索
  const filteredUsers = users.filter(u => {
    // 部署フィルター
    if (selectedDepartmentId !== 'all' && u.department_id?.toString() !== selectedDepartmentId) {
      return false;
    }
    // 検索フィルター
    if (userSearchQuery && !u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) &&
        !u.username.toLowerCase().includes(userSearchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  // 部署のユニークリストを取得
  const departments = Array.from(new Set(users.map(u => u.department_id).filter(Boolean)))
    .map(deptId => {
      const user = users.find(u => u.department_id === deptId);
      return { id: deptId, name: user?.department_name || '不明' };
    });

  const loadPendingRequests = async () => {
    try {
      const requests = await attendanceCorrectionService.getRequests();
      const pendingMap = new Map<number, AttendanceCorrectionRequest>();

      requests
        .filter(req => req.status === 'pending' && req.attendance_id !== null)
        .forEach(req => {
          // 同じattendance_idに対して複数のpending申請がある場合、最新のものを保持
          const existing = pendingMap.get(req.attendance_id!);
          if (!existing || new Date(req.created_at) > new Date(existing.created_at)) {
            pendingMap.set(req.attendance_id!, req);
          }
        });

      setPendingRequests(pendingMap);
    } catch (err: any) {
      console.error('申請一覧取得エラー:', err);
    }
  };

  const loadAttendanceRecords = async () => {
    if (!user || !tempSelectedUserId) return;

    try {
      setIsLoading(true);

      // 表示時に実際の値を更新
      setSelectedUserId(tempSelectedUserId);
      setFilterMonth(tempFilterMonth);
      setFilterDateFrom(tempFilterDateFrom);
      setFilterDateTo(tempFilterDateTo);

      const query: any = { user_id: tempSelectedUserId };

      // ローカル日付をYYYY-MM-DD形式に変換（タイムゾーンずれを防ぐ）
      const formatLocalDate = (date: Date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };

      // 月選択が優先（月が選択されている場合は日付範囲を無視）
      if (tempFilterMonth) {
        const [year, month] = tempFilterMonth.split('-');
        const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        const endDate = new Date(parseInt(year), parseInt(month), 0);
        query.date_from = formatLocalDate(startDate);
        query.date_to = formatLocalDate(endDate);
      } else if (tempFilterDateFrom || tempFilterDateTo) {
        // 日付範囲指定
        if (tempFilterDateFrom) query.date_from = tempFilterDateFrom;
        if (tempFilterDateTo) query.date_to = tempFilterDateTo;
      }

      const data = await attendanceService.getAttendanceRecords(query);
      setRecords(data);
    } catch (err: any) {
      setError(err.response?.data?.error || '勤怠記録の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      weekday: 'short'
    });
  };

  const formatTime = (timeString: string | null) => {
    if (!timeString) return '---';
    return new Date(timeString).toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 退勤時刻の表示（日跨ぎの場合は日付も表示）
  const formatClockOutTime = (clockIn: string | null, clockOut: string | null) => {
    if (!clockOut) return '---';

    const clockOutDate = new Date(clockOut);
    const clockOutTimeStr = clockOutDate.toLocaleTimeString('ja-JP', {
      hour: '2-digit',
      minute: '2-digit'
    });

    // 出勤時刻がない場合は時刻のみ表示
    if (!clockIn) return clockOutTimeStr;

    // 出勤時刻と退勤時刻の日付が異なる場合は日付も表示
    const clockInDate = new Date(clockIn);
    const clockInDay = clockInDate.toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' });
    const clockOutDay = clockOutDate.toLocaleDateString('ja-JP', { month: '2-digit', day: '2-digit' });

    if (clockInDay !== clockOutDay) {
      return `${clockOutDay} ${clockOutTimeStr}`;
    }

    return clockOutTimeStr;
  };

  const formatLeaveInfo = (leaveInfo: Array<{ leaveTypeId: number; leaveTypeName: string; hours: number }> | null | undefined): string => {
    if (!leaveInfo || leaveInfo.length === 0) return '-';

    return leaveInfo.map(leave => {
      // 時間を日数+時間に変換（1日=8時間）
      const days = Math.floor(leave.hours / 8);
      const remainingHours = leave.hours % 8;

      if (days > 0 && remainingHours > 0) {
        return `${leave.leaveTypeName}(${days}日${remainingHours}h)`;
      } else if (days > 0) {
        return `${leave.leaveTypeName}(${days}日)`;
      } else {
        return `${leave.leaveTypeName}(${remainingHours}h)`;
      }
    }).join(', ');
  };

  // 備考列のフォーマット（遅刻・早退情報を表示）
  const formatRemarksColumn = (record: AttendanceRecord): string => {
    const remarks: string[] = [];

    // 遅刻・早退情報
    if (record.is_late) {
      remarks.push('遅刻');
    }
    if (record.is_early_leave) {
      remarks.push('早退');
    }

    return remarks.length > 0 ? remarks.join(', ') : '-';
  };

  const calculateTotalLeaveHours = (): number => {
    let totalLeaveHours = 0;
    const counted = new Set<string>();

    for (const record of records) {
      if (!record.leave_info || record.leave_info.length === 0) continue;

      const key = `${record.user_id}_${record.date}`;
      if (counted.has(key)) continue;
      counted.add(key);

      for (const leave of record.leave_info) {
        // hoursはすでに時間単位で保存されている
        totalLeaveHours += leave.hours;
      }
    }

    return totalLeaveHours;
  };

  const calculateWorkHours = (clockIn: string | null, clockOut: string | null, breakMinutes: number = 0, autoDeductedBreakMinutes: number = 0) => {
    if (!clockIn) return '-';
    if (!clockOut) return '勤務中';
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    const diffMs = end.getTime() - start.getTime();

    // 総休憩時間（分）
    const totalBreakMinutes = (breakMinutes || 0) + (autoDeductedBreakMinutes || 0);

    // 実働時間（ミリ秒）= 総勤務時間 - 休憩時間
    const workMs = diffMs - (totalBreakMinutes * 60 * 1000);

    const hours = Math.floor(workMs / (1000 * 60 * 60));
    const minutes = Math.floor((workMs % (1000 * 60 * 60)) / (1000 * 60));
    // 「3時間0分」は列を折り返させるだけで情報が増えない
    return minutes === 0 ? `${hours}時間` : `${hours}時間${minutes}分`;
  };

  // 終了日の最大値を計算（開始日から30日後）
  const getMaxDateTo = () => {
    if (!tempFilterDateFrom) return '';
    const fromDate = new Date(tempFilterDateFrom);
    fromDate.setDate(fromDate.getDate() + 30);
    return fromDate.toISOString().split('T')[0];
  };

  // 選択中の従業員の氏名を取得
  const getSelectedUserName = () => {
    const selectedUser = users.find(u => u.id === selectedUserId);
    return selectedUser?.name || user?.name || '';
  };

  const handleOpenCorrectionModal = (record: AttendanceRecord, isProxySubmit: boolean = false) => {
    // 代理申請の場合、recordにisProxyフラグを追加
    const recordWithProxyFlag = { ...record, isProxySubmit };
    setSelectedRecord(recordWithProxyFlag as any);
    setIsModalOpen(true);
    setError('');
    setSuccessMessage('');
  };

  const handleSubmitCorrection = async (data: {
    request_type: 'update' | 'delete' | 'create';
    reason: string;
    requested_clock_in?: string;
    requested_clock_out?: string;
    target_date?: string;
    target_user_id?: number;
  }) => {
    if (!selectedRecord) return;

    try {
      const requestData: any = { ...data };

      // update または delete の場合のみ attendance_id を含める
      if (data.request_type !== 'create' && selectedRecord.id) {
        requestData.attendance_id = selectedRecord.id;
      }

      await attendanceCorrectionService.createRequest(requestData);

      setSuccessMessage('修正申請を送信しました。管理者の承認をお待ちください。');
      setIsModalOpen(false);
      setSelectedRecord(null);

      // 申請一覧と勤怠記録を再読み込み
      await loadPendingRequests();
      await loadAttendanceRecords();

      // 成功メッセージを3秒後に消す
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err: any) {
      throw err; // モーダル側でエラーを処理
    }
  };

  const handleDeleteCorrection = async (id: number) => {
    try {
      await attendanceCorrectionService.deleteRequest(id);
      setSuccessMessage('修正申請を取り下げました。');
      setIsModalOpen(false);
      setSelectedRecord(null);

      // 申請一覧と勤怠記録を再読み込み
      await loadPendingRequests();
      await loadAttendanceRecords();

      // 成功メッセージを3秒後に消す
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err: any) {
      throw err; // モーダル側でエラーを処理
    }
  };

  if (isLoading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>読み込み中...</div>;
  }

  return (
    /*
     * ⚠️ **この画面だけ広く取る。** 14列の記録表なので、他の画面と同じ 1000px に
     * 収めると**画面が広いのに横スクロールが出る**(代表指摘)。
     * 表が収まる幅まで許し、それ以上は広げない(1行が長くなりすぎると目が滑る)。
     */
    <div style={{ maxWidth: '1480px', margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' }}>
      <header style={{ marginBottom: 'clamp(15px, 4vw, 30px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
            <h1 style={{ fontSize: 'clamp(18px, 5vw, 24px)', margin: 0 }}>勤怠履歴</h1>
            {user && (
              <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                {user.name}さん ({user.companyCode})
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: 'clamp(6px, 1.5vw, 8px) clamp(12px, 3vw, 16px)',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: 'clamp(12px, 2.5vw, 14px)',
              whiteSpace: 'nowrap'
            }}
          >
            ダッシュボードに戻る
          </button>
        </div>

        {/* フィルターエリア */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 'clamp(10px, 2vw, 15px)',
          padding: 'clamp(12px, 3vw, 20px)',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          {/* 従業員選択（権限がある場合のみ） */}
          {user && permissions && permissions.attendanceViewScope !== 'self' && users.length > 0 && (
            <>
              {/* 部署選択 */}
              <div>
                <label htmlFor="dept-filter" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  部署
                </label>
                <select
                  id="dept-filter"
                  value={selectedDepartmentId}
                  onChange={(e) => setSelectedDepartmentId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">すべての部署</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id ?? ''}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 従業員検索 */}
              <div>
                <label htmlFor="user-search" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  従業員検索
                </label>
                <input
                  id="user-search"
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="氏名またはログインID"
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)'
                  }}
                />
              </div>

              {/* 従業員選択 */}
              <div>
                <label htmlFor="user-filter" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
                  従業員
                </label>
                <div style={{ marginBottom: '8px' }}>
                  <EmploymentFilter
                    id="history-employment"
                    value={employment}
                    onChange={setEmployment}
                    count={users.length}
                  />
                </div>
                <select
                  id="user-filter"
                  value={tempSelectedUserId || ''}
                  onChange={(e) => setTempSelectedUserId(e.target.value ? Number(e.target.value) : user.id)}
                  style={{
                    width: '100%',
                    padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    cursor: 'pointer',
                    maxHeight: '200px'
                  }}
                  size={1}
                >
                  {filteredUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.employee_number} {u.name}{u.retired_at ? '（退職）' : ''} {u.department_name ? `- ${u.department_name}` : ''}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                  {filteredUsers.length}人表示中 / 全{users.length}人
                </small>
              </div>
            </>
          )}

          {/* 月選択 */}
          <div>
            <label htmlFor="month-filter" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
              月選択
            </label>
            <input
              id="month-filter"
              type="month"
              value={tempFilterMonth}
              onChange={(e) => {
                setTempFilterMonth(e.target.value);
                // 月選択時は日付範囲をクリア・グレーアウト
                if (e.target.value) {
                  setTempFilterDateFrom('');
                  setTempFilterDateTo('');
                }
              }}
              disabled={!!tempFilterDateFrom || !!tempFilterDateTo}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                backgroundColor: (tempFilterDateFrom || tempFilterDateTo) ? '#e9ecef' : 'white',
                cursor: (tempFilterDateFrom || tempFilterDateTo) ? 'not-allowed' : 'pointer'
              }}
            />
          </div>

          {/* 日付範囲（開始） */}
          <div>
            <label htmlFor="date-from" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
              開始日
            </label>
            <input
              id="date-from"
              type="date"
              value={tempFilterDateFrom}
              onChange={(e) => {
                setTempFilterDateFrom(e.target.value);
                // 開始日選択時は月選択をクリア・グレーアウト
                if (e.target.value) {
                  setTempFilterMonth('');
                  // 終了日が30日以上先の場合はクリア
                  if (tempFilterDateTo) {
                    const fromDate = new Date(e.target.value);
                    const toDate = new Date(tempFilterDateTo);
                    const diffDays = Math.floor((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
                    if (diffDays > 30) {
                      setTempFilterDateTo('');
                    }
                  }
                }
              }}
              disabled={!!tempFilterMonth}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                backgroundColor: tempFilterMonth ? '#e9ecef' : 'white',
                cursor: tempFilterMonth ? 'not-allowed' : 'pointer'
              }}
            />
          </div>

          {/* 日付範囲（終了） */}
          <div>
            <label htmlFor="date-to" style={{ display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: 'clamp(12px, 3vw, 14px)' }}>
              終了日
            </label>
            <input
              id="date-to"
              type="date"
              value={tempFilterDateTo}
              onChange={(e) => {
                setTempFilterDateTo(e.target.value);
                // 終了日選択時は月選択をクリア・グレーアウト
                if (e.target.value) {
                  setTempFilterMonth('');
                }
              }}
              disabled={!!tempFilterMonth}
              min={tempFilterDateFrom || undefined}
              max={getMaxDateTo() || undefined}
              style={{
                width: '100%',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                backgroundColor: tempFilterMonth ? '#e9ecef' : 'white',
                cursor: tempFilterMonth ? 'not-allowed' : 'pointer'
              }}
            />
            {tempFilterDateFrom && (
              <small style={{ color: '#6c757d', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                ※ 開始日から30日以内まで選択可能
              </small>
            )}
          </div>

          {/* 表示ボタン */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={loadAttendanceRecords}
              disabled={!tempFilterMonth && !(tempFilterDateFrom && tempFilterDateTo)}
              style={{
                flex: 1,
                minWidth: '80px',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                backgroundColor: (!tempFilterMonth && !(tempFilterDateFrom && tempFilterDateTo)) ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: (!tempFilterMonth && !(tempFilterDateFrom && tempFilterDateTo)) ? 'not-allowed' : 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontWeight: '500',
                opacity: (!tempFilterMonth && !(tempFilterDateFrom && tempFilterDateTo)) ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => {
                if (tempFilterMonth || (tempFilterDateFrom && tempFilterDateTo)) {
                  e.currentTarget.style.backgroundColor = '#0056b3';
                }
              }}
              onMouseOut={(e) => {
                if (tempFilterMonth || (tempFilterDateFrom && tempFilterDateTo)) {
                  e.currentTarget.style.backgroundColor = '#007bff';
                }
              }}
            >
              表示
            </button>
            <button
              onClick={() => {
                setTempFilterMonth('');
                setFilterMonth('');
                setTempFilterDateFrom('');
                setFilterDateFrom('');
                setTempFilterDateTo('');
                setFilterDateTo('');
                setTempSelectedUserId(user?.id || null);
                setSelectedUserId(user?.id || null);
                setSelectedDepartmentId('all');
                setUserSearchQuery('');
                setRecords([]); // データをクリア（期間なしなので表示しない）
              }}
              style={{
                flex: 1,
                minWidth: '80px',
                padding: 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: 'clamp(12px, 2.5vw, 14px)',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#5a6268'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#6c757d'}
            >
              クリア
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d', backgroundColor: 'white', borderRadius: '8px' }}>
          勤怠記録がまだありません
        </div>
      ) : (
        <div>
        {/*
          14列ある記録の表なので、狭い幅に押し込むと各セルが2行3行に折れて読めなくなる
          (「1時間0分 (自動)」「3時間0分」あたりが特に折れていた)。
          **折り返さず横スクロールに逃がす**。表は読むものであって、詰めるものではない。
        */}
        <style>{`
          .att-history th, .att-history td { white-space: nowrap; }
        `}</style>
        {/*
          高さも固定 600px だと、縦に広い画面で表の下が余る。画面の高さに追従させ、
          小さい画面では今までどおり 600px を下限にする
        */}
        <div style={{ overflowX: 'auto', maxHeight: 'max(600px, 62vh)', overflowY: 'auto' }}>
          <table className="att-history" style={{
            width: '100%',
            minWidth: '1080px',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>従業員</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>日付</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>出勤時刻</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>退勤時刻</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>休憩</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>勤務時間</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(10px, 2vw, 12px)' }}>所定内</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(10px, 2vw, 12px)' }}>時間外</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(10px, 2vw, 12px)' }}>休日労働</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(10px, 2vw, 12px)' }}>休日時間外</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(10px, 2vw, 12px)' }}>深夜勤務</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>休暇</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>備考</th>
                <th style={{ padding: 'clamp(8px, 2vw, 12px)', textAlign: 'left', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => {
                /*
                 * 中抜けや打刻忘れで **1日に複数行**が出る。日付をそのまま繰り返すと
                 * ぱっと見が別の日のデータに見えるので、同じ人・同じ日が続く間は
                 * ひとかたまりに見せる。判定は隣接で行う(サーバ側が
                 * date → user_id → clock_in の順で返すので、同じ日は必ず隣り合う)。
                 */
                const dayKey = (r: any) => `${r?.user_id ?? ''}_${String(r?.date ?? '').split('T')[0]}`;
                const key = dayKey(record);
                const isFirstOfDay = index === 0 || dayKey(records[index - 1]) !== key;
                const isLastOfDay = index === records.length - 1 || dayKey(records[index + 1]) !== key;
                const sameDayCount = records.filter((r) => dayKey(r) === key).length;
                const isGrouped = sameDayCount > 1;

                return (
                <tr key={record.id || `empty-${record.date}-${index}`} style={{
                  // 同じ日の途中は区切りを弱め、日の終わりだけしっかり切る
                  borderBottom: isLastOfDay ? '1px solid #dee2e6' : '1px dashed #e9ecef',
                  backgroundColor: record.is_holiday ? '#fff3cd' : (!record.id ? '#f8f9fa' : 'white')
                }}>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>{isFirstOfDay ? getSelectedUserName() : ''}</td>
                  <td style={{
                    padding: 'clamp(8px, 2vw, 12px)',
                    fontSize: 'clamp(12px, 2.5vw, 14px)',
                    // 同じ日のかたまりであることを、左の縦線でも示す
                    borderLeft: isGrouped ? '3px solid #2563EB' : '3px solid transparent',
                  }}>
                    {isFirstOfDay ? (
                      <>
                        {formatDate(record.date)}
                        {record.is_holiday && <span style={{ marginLeft: '4px', fontSize: '0.85em', color: '#856404' }}>(休日)</span>}
                        {isGrouped && (
                          <span
                            title="この日は複数回の勤務があります"
                            style={{
                              marginLeft: '6px',
                              fontSize: '0.75em',
                              fontWeight: 700,
                              color: '#1D4ED8',
                              background: '#DBEAFE',
                              borderRadius: '4px',
                              padding: '1px 6px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {sameDayCount}回
                          </span>
                        )}
                      </>
                    ) : (
                      // 2件目以降は空にする。左の縦線と破線の区切りでかたまりは読める
                      ''
                    )}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>{formatTime(record.clock_in)}{record.kiosk_actions?.includes('clock_in') && <KioskBadge deviceName={record.kiosk_device_name} />}</td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>{formatClockOutTime(record.clock_in, record.clock_out)}{record.kiosk_actions?.includes('clock_out') && <KioskBadge deviceName={record.kiosk_device_name} />}</td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>
                    {(() => {
                      const breakMinutes = (record.break_minutes || 0) + (record.auto_deducted_break_minutes || 0);
                      if (breakMinutes === 0) return '-';
                      const hours = Math.floor(breakMinutes / 60);
                      const mins = breakMinutes % 60;
                      const auto = record.auto_deducted_break_minutes ? '(自動)' : '';
                      if (hours > 0) {
                        return `${hours}時間${mins === 0 ? '' : `${mins}分`}${auto}`;
                      }
                      return `${mins}分${auto}`;
                    })()}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>{calculateWorkHours(record.clock_in, record.clock_out, record.break_minutes, record.auto_deducted_break_minutes)}</td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(10px, 2vw, 12px)' }}>
                    {record.regular_hours ? `${record.regular_hours}h` : '-'}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(10px, 2vw, 12px)', color: record.overtime_hours ? '#dc3545' : '#6c757d' }}>
                    {record.overtime_hours ? `${record.overtime_hours}h` : '-'}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(10px, 2vw, 12px)', color: record.holiday_work_hours ? '#fd7e14' : '#6c757d' }}>
                    {record.holiday_work_hours ? `${record.holiday_work_hours}h` : '-'}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(10px, 2vw, 12px)', color: record.holiday_overtime_hours ? '#dc3545' : '#6c757d' }}>
                    {record.holiday_overtime_hours ? `${record.holiday_overtime_hours}h` : '-'}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(10px, 2vw, 12px)', color: record.night_shift_hours ? '#6f42c1' : '#6c757d' }}>
                    {record.night_shift_hours ? `${record.night_shift_hours}h` : '-'}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2vw, 13px)', color: record.leave_info && record.leave_info.length > 0 ? '#28a745' : '#6c757d' }}>
                    {formatLeaveInfo(record.leave_info)}
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)', fontSize: 'clamp(11px, 2vw, 13px)' }}>
                    <span style={{ color: (record.is_late || record.is_early_leave) ? '#dc3545' : (record.leave_info && record.leave_info.length > 0 ? '#28a745' : '#6c757d') }}>
                      {formatRemarksColumn(record)}
                    </span>
                  </td>
                  <td style={{ padding: 'clamp(8px, 2vw, 12px)' }}>
                    {(() => {
                      /*
                       * ⚠️ **`!record.id` を「自分の記録」の条件に入れてはいけない。**
                       *
                       * 勤怠が無い日は id を持たない空行として作られる。それを
                       * 自分の記録とみなしていたため、**他人の勤怠を見ているのに
                       * 打刻の無い日だけ「修正申請」ボタンが出ていた**（本来は代理申請）。
                       * 空行にも user_id は入っている（routes/attendance.ts の空レコード生成）
                       * ので、持ち主の判定はそちらだけで足りる。
                       */
                      const isOwnRecord = record.user_id === user?.id;
                      const canProxySubmit = permissions?.canSubmitForOthers && !isOwnRecord;

                      // 自分の記録または代理申請権限がない場合、常にボタンを表示
                      if (!isOwnRecord && !canProxySubmit) {
                        return <span style={{ color: '#6c757d', fontSize: 'clamp(12px, 2.5vw, 14px)' }}>-</span>;
                      }

                      const hasPending = record.id && pendingRequests.has(record.id);

                      return (
                        <div style={{ display: 'flex', gap: 'clamp(4px, 1.5vw, 8px)', flexWrap: 'wrap' }}>
                          {/* 自分の記録の修正申請ボタン */}
                          {isOwnRecord && (
                            <button
                              onClick={() => handleOpenCorrectionModal(record)}
                              style={{
                                padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                backgroundColor: hasPending ? '#17a2b8' : '#ffc107',
                                color: hasPending ? 'white' : '#212529',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 13px)',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = hasPending ? '#138496' : '#e0a800';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = hasPending ? '#17a2b8' : '#ffc107';
                              }}
                            >
                              {hasPending ? '修正申請中' : '修正申請'}
                            </button>
                          )}

                          {/* 代理申請ボタン（代理申請権限があり、他人の記録の場合） */}
                          {canProxySubmit && (
                            <button
                              onClick={() => handleOpenCorrectionModal(record, true)}
                              style={{
                                padding: 'clamp(4px, 1.5vw, 6px) clamp(8px, 2vw, 12px)',
                                backgroundColor: hasPending ? '#17a2b8' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 'clamp(10px, 2vw, 13px)',
                                fontWeight: '500',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = hasPending ? '#138496' : '#218838';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = hasPending ? '#17a2b8' : '#28a745';
                              }}
                            >
                              {hasPending ? '申請中' : '代理申請'}
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      {/* 統計情報 */}
      <div style={{
        marginTop: 'clamp(10px, 3vw, 20px)',
        padding: 'clamp(12px, 3vw, 15px) clamp(15px, 4vw, 20px)',
        backgroundColor: 'white',
        borderRadius: '4px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'clamp(10px, 3vw, 15px)', fontSize: 'clamp(12px, 2.5vw, 14px)', color: '#495057' }}>
          <div>
            <span style={{ fontWeight: '500' }}>勤務日数: </span>
            <span>{records.filter(r => r.clock_in && r.clock_out).length}日</span>
          </div>
          <div>
            <span style={{ fontWeight: '500' }}>休日出勤: </span>
            <span style={{ color: '#fd7e14' }}>{records.filter(r => r.is_holiday && r.clock_out).length}日</span>
          </div>
          <div>
            <span style={{ fontWeight: '500' }}>所定内労働: </span>
            <span>{records.filter(r => r.clock_out).reduce((sum, r) => sum + (r.regular_hours || 0), 0).toFixed(2)}h</span>
          </div>
          <div>
            <span style={{ fontWeight: '500', color: '#dc3545' }}>時間外労働: </span>
            <span style={{ color: '#dc3545' }}>{records.filter(r => r.clock_out).reduce((sum, r) => sum + (r.overtime_hours || 0), 0).toFixed(2)}h</span>
          </div>
          <div>
            <span style={{ fontWeight: '500', color: '#fd7e14' }}>休日労働: </span>
            <span style={{ color: '#fd7e14' }}>{records.filter(r => r.clock_out).reduce((sum, r) => sum + (r.holiday_work_hours || 0), 0).toFixed(2)}h</span>
          </div>
          <div>
            <span style={{ fontWeight: '500', color: '#dc3545' }}>休日時間外: </span>
            <span style={{ color: '#dc3545' }}>{records.filter(r => r.clock_out).reduce((sum, r) => sum + (r.holiday_overtime_hours || 0), 0).toFixed(2)}h</span>
          </div>
          <div>
            <span style={{ fontWeight: '500' }}>合計勤務時間: </span>
            <span>
              {(() => {
                const totalMs = records
                  .filter(r => r.clock_in && r.clock_out)
                  .reduce((sum, r) => {
                    const start = new Date(r.clock_in!).getTime();
                    const end = new Date(r.clock_out!).getTime();
                    const totalBreakMinutes = (r.break_minutes || 0) + (r.auto_deducted_break_minutes || 0);
                    return sum + (end - start) - (totalBreakMinutes * 60 * 1000);
                  }, 0);
                const hours = totalMs / (1000 * 60 * 60);
                return hours.toFixed(2);
              })()}h
            </span>
          </div>
          <div>
            <span style={{ fontWeight: '500', color: '#6f42c1' }}>深夜勤務: </span>
            <span style={{ color: '#6f42c1' }}>{records.filter(r => r.clock_out).reduce((sum, r) => sum + (r.night_shift_hours || 0), 0).toFixed(2)}h</span>
          </div>
          <div>
            <span style={{ fontWeight: '500', color: '#28a745' }}>有給休暇時間: </span>
            <span style={{ color: '#28a745' }}>{calculateTotalLeaveHours().toFixed(2)}h</span>
          </div>
        </div>
      </div>
      </div>
      )}

      {/* 修正申請モーダル */}
      {selectedRecord && (
        <AttendanceCorrectionModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedRecord(null);
          }}
          record={selectedRecord}
          existingRequest={pendingRequests.get(selectedRecord.id)}
          onSubmit={handleSubmitCorrection}
          onDelete={handleDeleteCorrection}
        />
      )}
    </div>
  );
};

export default AttendanceHistoryPage;
